import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, Compass, Megaphone, Users, Bot, TrendingUp, TrendingDown,
  ArrowUpRight, Plane, PieChart, Award, Scale,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { OPPORTUNITIES } from "@/lib/demoData";
import { recommendationsFor } from "@/lib/recommendations";
import { deriveOpportunities, deriveOpportunityPortfolio } from "@/lib/opportunityEngine";
import { actionMarketLabel, buildActionContext, campaignPrefillFrom } from "@/lib/actionContext";
import { LodgeAiDrawer } from "@/components/LodgeAiDrawer";
import { useMarketIntelligence } from "@/lib/useMarketIntelligence";
import AirInsights from "@/components/dashboard/AirInsights";
import DestinationDecisionOverview from "@/components/dashboard/DestinationDecisionOverview";
import MarketDecisionOverview from "@/components/dashboard/MarketDecisionOverview";
import { fmtPaxStr, fmtRevStr, localizeCountry } from "@/lib/marketFormat";
import { buildMarketContext, makeProvenance, BENCHMARK_ELIGIBLE_LEVELS } from "@/lib/marketContext";
import {
  SectionCard, AirlineRanking, MonthlyEvolutionChart,
  SeasonalityChart, VisitorOriginsDonut, EconomicContextCard, DataSourcesCard,
  LodgeIntelligenceSignals, LiveMarketSignals, TourismKpiGrid, OccupancyChart,
  CrossDataInsights, UnavailableBlock,
} from "@/components/dashboard/parts";
import {
  buildCrossDataInsights, buildTourismDashboardView, formatTourismKpis,
} from "@/lib/tourismDashboard";
import { isMauritiusDestination, preferredMarketKey } from "@/lib/mauritiusDemoJourney";
import { isMauritiusDemoOrg, DATA_UNAVAILABLE, DEMO_MAURITIUS_ROUTES } from "@/lib/demoMauritius";
import { comparableMarketRank, findRouteMarket, marketPeriodLabel, marketSeasonalitySeries } from "@/lib/dashboardContext";
import { useBspAgencyData } from "@/lib/useBspAgencyData";
import { economicProviders, formatEconomicIndicators } from "@/lib/economicContext";
import { buildMacroTourismSignals } from "@/lib/macroTourismSignals";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
      <p className="text-[11px] font-medium text-slate-400">{label}</p>
      <p className="font-mono text-sm font-semibold text-[#1E3D2B]">{payload[0].value}</p>
    </div>
  );
};

const ActionButton = ({ icon: Icon, label, sub, onClick, disabled, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    disabled={disabled}
    className={`group flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
      disabled
        ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-70"
        : "border-slate-200 bg-white hover:border-[#3E7256] hover:shadow-sm"
    }`}
  >
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${disabled ? "bg-slate-100 text-slate-400" : "bg-lodge-mint/20 text-lodge-deep"}`}>
      <Icon size={17} />
    </span>
    <span className="flex-1">
      <span className="flex items-center gap-1.5 text-sm font-semibold text-[#1E3D2B]">
        {label}
        {disabled && <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-500">Bientôt</span>}
      </span>
      <span className="mt-0.5 block text-xs text-slate-400">{sub}</span>
    </span>
    {!disabled && <ArrowRight size={15} className="mt-1 shrink-0 text-slate-300 transition-colors group-hover:text-[#3E7256]" />}
  </button>
);

// Phase 3B.3 — Opportunity card visuals.
const OPP_ICON = { growth: TrendingUp, decline: TrendingDown, dominant: Award, concentration: PieChart, air: Plane, benchmark_gap: Scale, demo: Sparkles };
const PROV_PILL = {
  "Client Data": "bg-blue-50 text-blue-600",
  "Lodge Data": "bg-lodge-mint/20 text-lodge-deep",
  ADI: "bg-indigo-50 text-indigo-600",
  Benchmark: "bg-violet-50 text-violet-600",
  Demo: "bg-amber-100 text-amber-700",
};
const CONF_PILL = { High: "bg-emerald-50 text-emerald-700", Medium: "bg-amber-50 text-amber-700", Low: "bg-slate-100 text-slate-500" };

const EMPTY_ORG = {};

export default function Dashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeMembership, activeOrgId } = useAuth();
  const org = activeMembership?.org || EMPTY_ORG;
  const objectives = org.objectives || [];
  const isMauritius = isMauritiusDestination(org);
  const immutableDemo = org.is_demo && org.demo_mode === "immutable";
  // Demo Maurice only: illustrative fallbacks are replaced by explicit "unavailable" states.
  const mauritiusDemo = isMauritiusDemoOrg(org);
  const demoUnavailable = (testid, detail, provenance) => (
    <UnavailableBlock testid={testid} title={DATA_UNAVAILABLE} detail={detail} provenance={provenance} />
  );
  const marketStorageKey = `lodge_dash_market:${activeOrgId || "unknown"}`;
  const urlParams = new URLSearchParams(location.search);
  const urlMarket = urlParams.get("market");
  const urlScope = urlParams.get("view") === "destination" ? "destination" : "market";
  const [market, setMarket] = useState(() => {
    if (urlMarket) return urlMarket;
    if (!immutableDemo) return "FR-WORLD";
    try { return sessionStorage.getItem(marketStorageKey) || "FR-MRU"; }
    catch { return "FR-MRU"; }
  });
  const changeMarket = (value) => {
    setMarket(value);
    if (immutableDemo) {
      try { sessionStorage.setItem(marketStorageKey, value); } catch { /* storage unavailable */ }
    }
    navigate({ pathname: "/dashboard", search: `?view=market&market=${encodeURIComponent(value)}` }, { replace: true });
  };
  const analysisScope = urlScope;
  const changeAnalysisScope = (value) => {
    const search = value === "destination"
      ? "?view=destination"
      : `?view=market&market=${encodeURIComponent(market)}`;
    navigate({ pathname: "/dashboard", search }, { replace: true });
  };
  const [dataLens, setDataLens] = useState("all");
  const { markets, data: MI, airLive, airLoading } = useMarketIntelligence(market);
  const bspEnabled = analysisScope === "market" && market === "FR-MRU";
  const { data: bspData, loading: bspLoading } = useBspAgencyData("MU", bspEnabled);
  const selectedRoute = markets.find((m) => m.value === market);
  const marketLabel = selectedRoute?.label;
  const marketScopeActive = analysisScope === "market" && market !== "FR-WORLD";
  // The market catalogue exposes human-readable origins (for example "India")
  // while the organisation market endpoint is keyed by the ISO origin ("IN").
  // Derive that key from the canonical route value instead of the display label.
  const signalMarketKey = String(selectedRoute?.value || market || "")
    .split("-")[0]
    .toUpperCase();
  const destinationScopeLabel = "Destination globale · indépendant du marché sélectionné";
  const demoScopeLabel = marketScopeActive
    ? "Démonstration · non spécifique au marché"
    : "Destination globale · démonstration";
  const initializedMarketOrg = useRef(null);

  // Treat the URL as the source of truth when the same mounted dashboard receives
  // a shared link (or browser navigation). React Router can update `location`
  // without remounting this component, so state initialisers alone are insufficient.
  useEffect(() => {
    if (urlScope === "market" && urlMarket && urlMarket !== market) {
      setMarket(urlMarket);
      if (immutableDemo) {
        try { sessionStorage.setItem(marketStorageKey, urlMarket); } catch { /* storage unavailable */ }
      }
    }
  }, [immutableDemo, market, marketStorageKey, urlMarket, urlScope]);

  // Keep both dashboard modes directly shareable. Destination URLs remain clean,
  // while market URLs always carry the canonical route selected in the fixed bar.
  useEffect(() => {
    if (location.pathname !== "/dashboard") return;
    // Do not overwrite an incoming shared URL with stale state from the previously
    // displayed scope while the synchronisation effect above is catching up.
    if (analysisScope === "market" && urlMarket && market !== urlMarket) return;
    const params = new URLSearchParams();
    params.set("view", analysisScope);
    if (analysisScope === "market" && market) params.set("market", market);
    const nextSearch = `?${params.toString()}`;
    if (location.search !== nextSearch) {
      navigate({ pathname: "/dashboard", search: nextSearch }, { replace: true });
    }
  }, [analysisScope, location.pathname, location.search, market, navigate, urlMarket, urlScope]);

  useEffect(() => {
    if (!activeOrgId) {
      initializedMarketOrg.current = null;
      return;
    }
    if (initializedMarketOrg.current === activeOrgId) return;
    if (immutableDemo) {
      if (urlMarket) {
        setMarket(urlMarket);
        try { sessionStorage.setItem(marketStorageKey, urlMarket); } catch { /* storage unavailable */ }
        initializedMarketOrg.current = activeOrgId;
        return;
      }
      let saved = null;
      try { saved = sessionStorage.getItem(marketStorageKey); } catch { /* storage unavailable */ }
      if (saved) {
        setMarket(saved);
        initializedMarketOrg.current = activeOrgId;
        return;
      }
    }
    const preferred = immutableDemo
      ? (markets.some((candidate) => candidate?.value === "FR-MRU") ? "FR-MRU" : null)
      : preferredMarketKey(org, markets);
    if (!preferred) return;
    setMarket(preferred);
    initializedMarketOrg.current = activeOrgId;
  }, [activeOrgId, org, markets, immutableDemo, marketStorageKey, urlMarket]);

  // --- Real imported client data (unchanged endpoints) ---
  // Non-blocking data-availability error is tracked separately from "no data" so that a
  // transient API/network failure is never silently shown as an empty/demo state.
  const [dataError, setDataError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const retryData = () => { setDataError(false); setReloadKey((k) => k + 1); };

  const [importedState, setImportedState] = useState({ orgId: null, data: null });
  const [selectedType, setSelectedType] = useState(null);
  useEffect(() => {
    if (!activeOrgId) { setImportedState({ orgId: null, data: null }); return; }
    let ignore = false;
    setImportedState({ orgId: activeOrgId, data: null });
    const q = selectedType ? `?data_type=${encodeURIComponent(selectedType)}` : "";
    api.get(`/organizations/${activeOrgId}/dashboard-data${q}`)
      .then(({ data }) => { if (!ignore) setImportedState({ orgId: activeOrgId, data: data.has_data ? data : null }); })
      .catch(() => { if (!ignore) { setImportedState({ orgId: activeOrgId, data: null }); setDataError(true); } });
    return () => { ignore = true; };
  }, [activeOrgId, selectedType, reloadKey]);
  const imported = importedState.orgId === activeOrgId ? importedState.data : null;

  const [dimState, setDimState] = useState({ orgId: null, data: null });
  const [dimType, setDimType] = useState(null);
  useEffect(() => {
    if (!activeOrgId) { setDimState({ orgId: null, data: null }); return; }
    let ignore = false;
    setDimState({ orgId: activeOrgId, data: null });
    const q = dimType ? `?data_type=${encodeURIComponent(dimType)}` : "";
    api.get(`/organizations/${activeOrgId}/dimension-analysis${q}`)
      .then(({ data }) => { if (!ignore) setDimState({ orgId: activeOrgId, data: data.has_data ? data : null }); })
      .catch(() => { if (!ignore) { setDimState({ orgId: activeOrgId, data: null }); setDataError(true); } });
    return () => { ignore = true; };
  }, [activeOrgId, dimType, reloadKey]);
  const dim = dimState.orgId === activeOrgId ? dimState.data : null;

  useEffect(() => {
    setSelectedType(null);
    setDimType(null);
    setDataError(false);
  }, [activeOrgId]);

  const hasImported = !!imported;
  const hasRealData = hasImported || (dim && dim.categories.length > 0);

  // Phase 3B.2.a — National Benchmark (CITY/REGION only), shown as secondary CONTEXT (never PRIMARY).
  const [benchmarkState, setBenchmarkState] = useState({ orgId: null, data: null });
  useEffect(() => {
    if (!activeOrgId || !BENCHMARK_ELIGIBLE_LEVELS.has(org.geographic_level)) {
      setBenchmarkState({ orgId: activeOrgId || null, data: null });
      return;
    }
    let ignore = false;
    setBenchmarkState({ orgId: activeOrgId, data: null });
    api.get(`/organizations/${activeOrgId}/benchmark-data`)
      .then(({ data }) => { if (!ignore) setBenchmarkState({ orgId: activeOrgId, data: data?.has_benchmark ? data : null }); })
      .catch(() => { if (!ignore) setBenchmarkState({ orgId: activeOrgId, data: null }); });
    return () => { ignore = true; };
  }, [activeOrgId, org.geographic_level, reloadKey]);
  const benchmark = benchmarkState.orgId === activeOrgId ? benchmarkState.data : null;

  // Shared context (derived from existing profile only) + provenance for the benchmark block.
  const marketContext = buildMarketContext(org, market, MI.period.label);
  const benchmarkProvenance = benchmark
    ? makeProvenance({ source: "Lodge / INSEE", sourceType: "benchmark", role: "CONTEXT",
        geographyLevel: "COUNTRY", geographyEntity: benchmark.benchmark_scope?.entity,
        period: (benchmark.markets?.[0]?.volume_metric ? null : null), isDemo: false })
    : null;
  const benchTop = (benchmark?.markets || []).slice(0, 5);
  const benchMetric = benchmark?.markets?.[0]?.volume_metric || null;

  // Phase 3B.2.b — real Executive Snapshot + Market Pulse from markets-data (per-block resolution).
  const [mktState, setMktState] = useState({ orgId: null, data: null, loading: false });
  useEffect(() => {
    if (!activeOrgId) { setMktState({ orgId: null, data: null, loading: false }); return; }
    let ignore = false;
    setMktState({ orgId: activeOrgId, data: null, loading: true });
    api.get(`/organizations/${activeOrgId}/markets-data`)
      .then(({ data }) => {
        if (!ignore) setMktState({ orgId: activeOrgId, data: data?.has_data && data.markets?.length ? data : null, loading: false });
      })
      .catch(() => {
        if (!ignore) { setMktState({ orgId: activeOrgId, data: null, loading: false }); setDataError(true); }
      });
    return () => { ignore = true; };
  }, [activeOrgId, reloadKey]);
  const mkt = mktState.orgId === activeOrgId ? mktState.data : null;
  const mktLoading = !!activeOrgId && (mktState.orgId !== activeOrgId || mktState.loading);

  // Server-authoritative Lodge Intelligence for the selected market. The endpoint
  // enforces organisation membership and only derives signals from observed data.
  const [signalState, setSignalState] = useState({ orgId: null, marketKey: null, market: null, signals: [], loading: false, error: false, missing: false });
  useEffect(() => {
    if (!activeOrgId || !marketScopeActive) {
      setSignalState({ orgId: activeOrgId || null, marketKey: null, market: null, signals: [], loading: false, error: false, missing: false });
      return;
    }
    let ignore = false;
    setSignalState({ orgId: activeOrgId, marketKey: market, market: null, signals: [], loading: true, error: false, missing: false });
    api.get(`/organizations/${activeOrgId}/markets/${signalMarketKey}`)
      .then(({ data }) => {
        if (!ignore) setSignalState({
          orgId: activeOrgId,
          marketKey: market,
          market: data?.market || null,
          signals: Array.isArray(data?.signals) ? data.signals : [],
          loading: false,
          error: false,
          missing: false,
        });
      })
      .catch((error) => {
        if (!ignore) setSignalState({
          orgId: activeOrgId,
          marketKey: market,
          market: null,
          signals: [],
          loading: false,
          error: error?.response?.status !== 404,
          missing: error?.response?.status === 404,
        });
      });
    return () => { ignore = true; };
  }, [activeOrgId, market, marketScopeActive, reloadKey, signalMarketKey]);
  const currentSignals = signalState.orgId === activeOrgId && signalState.marketKey === market
    ? signalState.signals
    : [];
  const currentSignalMarket = signalState.orgId === activeOrgId && signalState.marketKey === market
    ? signalState.market
    : null;
  const signalsLoading = marketScopeActive && signalState.orgId === activeOrgId
    && signalState.marketKey === market && signalState.loading;
  const signalsError = marketScopeActive && signalState.orgId === activeOrgId
    && signalState.marketKey === market && signalState.error;
  const signalsMissing = marketScopeActive && signalState.orgId === activeOrgId
    && signalState.marketKey === market && signalState.missing;

  // Stored Lodge macro reference data for the selected emitting market.
  // The backend derives the country from the canonical market key and verifies
  // organization membership before returning any response.
  const [economicState, setEconomicState] = useState({ orgId: null, marketKey: null, data: null, loading: false, error: false });
  useEffect(() => {
    if (!activeOrgId || !marketScopeActive) {
      setEconomicState({ orgId: activeOrgId || null, marketKey: null, data: null, loading: false, error: false });
      return;
    }
    let ignore = false;
    setEconomicState({ orgId: activeOrgId, marketKey: market, data: null, loading: true, error: false });
    api.get(`/economic-context/markets/${encodeURIComponent(market)}`, {
      headers: { "X-Organization-ID": activeOrgId },
    }).then(({ data }) => {
      if (!ignore) setEconomicState({ orgId: activeOrgId, marketKey: market, data, loading: false, error: false });
    }).catch(() => {
      if (!ignore) setEconomicState({ orgId: activeOrgId, marketKey: market, data: null, loading: false, error: true });
    });
    return () => { ignore = true; };
  }, [activeOrgId, market, marketScopeActive, reloadKey]);
  const economicCurrent = economicState.orgId === activeOrgId && economicState.marketKey === market;
  const economicPayload = economicCurrent ? economicState.data : null;
  const economicItems = formatEconomicIndicators(economicPayload);
  const macroProviders = economicProviders(economicPayload);
  const economicLoading = marketScopeActive && economicCurrent && economicState.loading;
  const economicError = marketScopeActive && economicCurrent && economicState.error;

  // Stored, sourced sector news for the destination linked to the selected route.
  // Collection happens server-side under platform-admin control; members only
  // read a published snapshot and the UI states its destination-wide scope.
  const [newsState, setNewsState] = useState({ orgId: null, marketKey: null, data: null, loading: false, error: false });
  useEffect(() => {
    if (!activeOrgId || !marketScopeActive) {
      setNewsState({ orgId: activeOrgId || null, marketKey: null, data: null, loading: false, error: false });
      return;
    }
    let ignore = false;
    setNewsState({ orgId: activeOrgId, marketKey: market, data: null, loading: true, error: false });
    api.get(`/news-signals/markets/${encodeURIComponent(market)}`, {
      headers: { "X-Organization-ID": activeOrgId },
    }).then(({ data }) => {
      if (!ignore) setNewsState({ orgId: activeOrgId, marketKey: market, data, loading: false, error: false });
    }).catch(() => {
      if (!ignore) setNewsState({ orgId: activeOrgId, marketKey: market, data: null, loading: false, error: true });
    });
    return () => { ignore = true; };
  }, [activeOrgId, market, marketScopeActive, reloadKey]);
  const newsCurrent = newsState.orgId === activeOrgId && newsState.marketKey === market;
  const newsPayload = newsCurrent ? newsState.data : null;
  const newsItems = Array.isArray(newsPayload?.articles) ? newsPayload.articles : [];
  const newsLoading = marketScopeActive && newsCurrent && newsState.loading;
  const newsError = marketScopeActive && newsCurrent && newsState.error;
  const newsProviders = newsPayload?.dataset?.provider ? [newsPayload.dataset.provider] : [];
  // Cross two independently scoped, server-authoritative payloads. The helper
  // emits nothing unless both contain the observations required by a rule.
  const macroTourismSignals = economicCurrent
    ? buildMacroTourismSignals({ market: currentSignalMarket, economic: economicPayload })
    : [];
  const intelligenceSignals = [...currentSignals, ...macroTourismSignals];

  // Destination-wide Lodge tourism reference series (monthly tourism KPIs + occupancy).
  const [tourismState, setTourismState] = useState({ orgId: null, data: null, loading: false });
  useEffect(() => {
    if (!activeOrgId) { setTourismState({ orgId: null, data: null, loading: false }); return; }
    let ignore = false;
    setTourismState({ orgId: activeOrgId, data: null, loading: true });
    api.get(`/organizations/${activeOrgId}/tourism-dashboard-data`)
      .then(({ data }) => {
        if (!ignore) setTourismState({ orgId: activeOrgId, data: data?.has_data ? data : null, loading: false });
      })
      .catch(() => {
        if (!ignore) { setTourismState({ orgId: activeOrgId, data: null, loading: false }); setDataError(true); }
      });
    return () => { ignore = true; };
  }, [activeOrgId, reloadKey]);
  const tourismPayload = tourismState.orgId === activeOrgId ? tourismState.data : null;
  const tourismLoading = !!activeOrgId && (tourismState.orgId !== activeOrgId || tourismState.loading);
  const tourismView = buildTourismDashboardView(tourismPayload);

  const mktRows = (mkt?.markets || []).slice().sort((a, b) => (b.volume || 0) - (a.volume || 0));
  const realMarkets = mktRows.length > 0;
  const mktLayer = realMarkets && mktRows.some(r => r.source_layer === "CLIENT") ? "client" : "lodge";
  const mktMetric = mktRows.find(r => r.volume_metric)?.volume_metric || "Volume";
  const totalVolume = mktRows.reduce((s, r) => s + (r.volume || 0), 0);
  // Volume-weighted growth: only markets with a real evolution AND a real volume (>0). Never fabricated.
  const wRows = mktRows.filter(r => r.evolution != null && r.volume != null && r.volume > 0);
  const wVolSum = wRows.reduce((s, r) => s + r.volume, 0);
  const avgGrowth = wVolSum > 0 ? Math.round(wRows.reduce((s, r) => s + r.evolution * r.volume, 0) / wVolSum) : null;
  const snapshotProv = realMarkets
    ? makeProvenance({ source: mktLayer === "client" ? "Vos imports" : "Lodge", sourceType: mktLayer, role: "PRIMARY", period: MI.period.label })
    : makeProvenance({ source: "Démonstration", sourceType: "demo", role: "DEMO", isDemo: true });
  const sourceLabel = mktLayer === "client" ? "Client Data" : "Lodge Data";
  const fmtNum = (n) => new Intl.NumberFormat("fr-FR", { notation: Math.abs(n) >= 100000 ? "compact" : "standard", maximumFractionDigits: 1 }).format(n);
  const tourismFormatted = formatTourismKpis(tourismView);
  const receiptsFxSub = tourismView.kpis.receiptsFx
    ? `${tourismView.period || "Période indisponible"} · Converti de ${tourismView.kpis.receiptsFx.sourceCurrency} en USD · taux au ${tourismView.kpis.receiptsFx.asOf}`
    : tourismView.period || "Période indisponible";
  const tourismKpis = [
    { key: "arrivals", label: "Arrivées visiteurs", value: tourismFormatted.arrivals, unit: "", sub: tourismView.period || "Période indisponible", scope: "Destination globale" },
    { key: "averageStay", label: "Séjour moyen", value: tourismFormatted.averageStay, unit: "nuits", sub: "Moyenne pondérée par les arrivées", scope: "Destination globale" },
    { key: "receipts", label: "Recettes touristiques", value: tourismFormatted.receipts, unit: tourismView.kpis.receiptsCurrency || "", sub: receiptsFxSub, scope: "Destination globale" },
    { key: "roomOccupancy", label: "Occupation chambres", value: tourismFormatted.roomOccupancy, unit: "%", sub: tourismView.occupancyPeriod ? `Moyenne · ${tourismView.occupancyPeriod}` : "Période indisponible", scope: "Destination globale" },
  ];

  const selectedMarketRow = findRouteMarket(mktRows, market, selectedRoute);
  const selectedRank = comparableMarketRank(mktRows, selectedMarketRow);
  const selectedPeriod = marketPeriodLabel(selectedMarketRow, MI.period.label);
  const selectedMarketSeasonality = marketSeasonalitySeries(selectedMarketRow);
  const selectedMarketEvolution = selectedMarketSeasonality?.map((row) => ({
    month: row.month,
    visitors: row.arrivals,
  })) || null;
  const selectedMarketSource = selectedMarketRow?.sources?.seasonality === "CLIENT" ? "Client Data" : "Lodge Data";

  // Executive Snapshot KPIs — real when markets-data available (per-block), else demo fallback.
  const destinationSnapshotKpis = mktLoading
    ? {
        volume: { key: "volume", label: "Volume total", loading: true },
        growth: { key: "growth", label: "Croissance pondérée", loading: true },
        markets: { key: "markets", label: "Marchés analysés", loading: true },
        top: { key: "top", label: "Marché principal", loading: true },
      }
    : realMarkets ? {
        volume: { key: "volume", label: "Volume total", value: fmtNum(totalVolume), unit: "", sub: `${mktRows.length} marché${mktRows.length > 1 ? "s" : ""} · ${mktMetric}`, source: sourceLabel },
        growth: avgGrowth != null
          ? { key: "growth", label: "Croissance pondérée", value: `${avgGrowth > 0 ? "+" : ""}${avgGrowth}`, unit: "%", positive: avgGrowth >= 0, delta: "", deltaLabel: "6 derniers mois vs 6 précédents", tooltip: "Moyenne pondérée des évolutions entre les six derniers mois observés et les six mois précédents." }
          : { key: "growth", label: "Croissance pondérée", value: "—", unit: "", sub: "Non disponible", source: sourceLabel, tooltip: "Moyenne des évolutions des marchés, pondérée par leur volume." },
        markets: { key: "markets", label: "Marchés analysés", value: String(mktRows.length), unit: "", sub: mktMetric, source: sourceLabel },
        top: { key: "top", label: "Marché principal", value: mktRows[0].name, unit: "", sub: mktRows[0].volume != null ? fmtNum(mktRows[0].volume) : "—", source: sourceLabel },
      }
    : {
        volume: { key: "volume", label: "Volume total", pending: true, sub: "Donnée non disponible", actionLabel: "Importez vos données" },
        growth: { key: "growth", label: "Croissance pondérée", pending: true, sub: "Donnée non disponible", actionLabel: "Importez vos données" },
        markets: { key: "markets", label: "Marchés analysés", pending: true, sub: "Donnée non disponible", actionLabel: "Importez vos données" },
        top: { key: "top", label: "Marché principal", pending: true, sub: "Donnée non disponible", actionLabel: "Importez vos données" },
      };

  const marketSnapshotKpis = mktLoading
    ? {
        volume: { key: "volume", label: "Arrivées du marché", loading: true },
        growth: { key: "growth", label: "Évolution du marché", loading: true },
        share: { key: "share", label: "Part de la destination", loading: true },
        rank: { key: "rank", label: "Rang du marché", loading: true },
      }
    : selectedMarketRow ? {
        volume: { key: "volume", label: "Arrivées du marché", value: fmtNum(selectedMarketRow.volume), unit: "", sub: `${selectedPeriod} · ${selectedMarketRow.volume_metric || mktMetric}`, source: sourceLabel, scope: "Marché sélectionné" },
        growth: selectedMarketRow.evolution != null
          ? { key: "growth", label: "Évolution du marché", value: `${selectedMarketRow.evolution > 0 ? "+" : ""}${selectedMarketRow.evolution}`, unit: "%", positive: selectedMarketRow.evolution >= 0, deltaLabel: "6 derniers mois vs 6 précédents", scope: "Marché sélectionné", tooltip: "Évolution propre au marché d'origine sélectionné." }
          : { key: "growth", label: "Évolution du marché", value: "—", unit: "", sub: "Non disponible", source: sourceLabel, scope: "Marché sélectionné" },
        share: selectedMarketRow.part_destination != null
          ? { key: "share", label: "Part de la destination", value: String(selectedMarketRow.part_destination), unit: "%", sub: `Parmi les marchés en ${selectedMarketRow.volume_metric || mktMetric}`, source: sourceLabel, scope: "Marché sélectionné" }
          : { key: "share", label: "Part de la destination", value: "—", unit: "", sub: "Non comparable", source: sourceLabel, scope: "Marché sélectionné" },
        rank: selectedRank
          ? { key: "rank", label: "Rang du marché", value: `#${selectedRank.rank}`, unit: "", sub: `sur ${selectedRank.total} marché${selectedRank.total > 1 ? "s" : ""} comparables`, source: sourceLabel, scope: "Marché sélectionné" }
          : { key: "rank", label: "Rang du marché", value: "—", unit: "", sub: "Non disponible", source: sourceLabel, scope: "Marché sélectionné" },
      }
    : {
        volume: { key: "volume", label: "Arrivées du marché", pending: true, sub: `Aucune série touristique pour ${marketLabel || "ce marché"}`, actionLabel: "Donnée indisponible" },
        growth: { key: "growth", label: "Évolution du marché", pending: true, sub: "Donnée non disponible", actionLabel: "Donnée indisponible" },
        share: { key: "share", label: "Part de la destination", pending: true, sub: "Donnée non disponible", actionLabel: "Donnée indisponible" },
        rank: { key: "rank", label: "Rang du marché", pending: true, sub: "Donnée non disponible", actionLabel: "Donnée indisponible" },
      };
  const snapshotKpis = analysisScope === "market" ? marketSnapshotKpis : destinationSnapshotKpis;

  // Market Pulse — real "Origine des arrivées" from markets breakdown, else demo.
  const originsReal = realMarkets && totalVolume > 0;
  const topOrigins = (() => {
    if (!originsReal) return MI.visitorOrigins;
    const withVol = mktRows.filter((r) => (r.volume || 0) > 0);
    const top = withVol.slice(0, 5).map((r) => ({ name: r.name, value: Math.round((r.volume / totalVolume) * 100) }));
    const rest = withVol.slice(5).reduce((s, r) => s + (r.volume || 0), 0);
    if (rest > 0) top.push({ name: "Autres", value: Math.round((rest / totalVolume) * 100) });
    return top;
  })();

  // Phase 3B.3 — Opportunities & Insights from real markets-data (per-block provenance), demo fallback.
  const oppResult = deriveOpportunities({
    markets: mktRows,
    benchmark,
    objectives,
    sourceLabel,
    geoLevel: org.geographic_level,
    focusMarket: analysisScope === "market" ? selectedMarketRow : null,
  });
  const demoOpps = (objectives.length
    ? recommendationsFor(objectives, 5)
    : OPPORTUNITIES.slice(0, 5).map((o) => ({ title: o.title, category: o.category, potential: o.potential, objective: null }))
  ).map((o, i) => ({
    id: `demo-${i}`, type: "demo", signal: o.title, opportunity: o.title,
    evidence: o.objective ? `Objectif : ${o.objective}` : "Exemple de démonstration",
    recommendedAction: "Explorer les marchés", source: "Demo", confidence: null,
    market: null, marketKey: null, objective: o.objective,
  }));
  const oppsAreDemo = oppResult.isDemo;
  const opps = oppsAreDemo ? demoOpps : oppResult.opportunities;
  const destinationMarketScope = isMauritius
    ? (mauritiusDemo ? DEMO_MAURITIUS_ROUTES : markets
      .filter((route) => /-(MU|MRU)$/.test(String(route.value || "").toUpperCase()))
      .map((route) => ({ key: route.value, label: route.label })))
      .map((route) => findRouteMarket(mktRows, route.key, {
        value: route.key,
        label: route.label,
        origin: route.iso || String(route.key || "").split("-")[0],
      }))
      .filter((row, index, rows) => row && rows.indexOf(row) === index)
    : null;
  const destinationPortfolio = deriveOpportunityPortfolio({
    markets: mktRows,
    marketScope: destinationMarketScope,
    benchmark,
    objectives,
    sourceLabel,
    geoLevel: org.geographic_level,
  }).opportunities;
  // Best real opportunity = first of the already-scored ranking (no second scoring here).
  const bestOpp = !oppsAreDemo && opps.length ? opps[0] : null;
  const dashboardDataSources = (MI.dataSources || []).map((item) => {
    if (item.name === "Lodge Data" && airLive) {
      return { ...item, desc: "Données aériennes du marché sélectionné", status: "Connecté" };
    }
    if (item.name === "Offices du Tourisme" && realMarkets && mktLayer === "lodge") {
      return { ...item, desc: "Arrivées visiteurs par marché", status: "Connecté" };
    }
    if (item.name === "World Bank" && macroProviders.includes("World Bank")) {
      return { ...item, desc: "Contexte macroéconomique du marché émetteur", status: "Connecté" };
    }
    return item;
  });
  [
    ["World Bank", "Contexte macroéconomique du marché émetteur"],
    ["ECB", "Taux de change de référence"],
  ].forEach(([provider, desc]) => {
    if (macroProviders.includes(provider) && !dashboardDataSources.some((item) => item.name === provider)) {
      dashboardDataSources.push({ name: provider, desc, status: "Connecté" });
    }
  });
  if (newsItems.length > 0 && !dashboardDataSources.some((item) => item.name === "Actualités sectorielles")) {
    dashboardDataSources.push({ name: "Actualités sectorielles", desc: "Articles datés et reliés à leur éditeur", status: "Connecté" });
  } else if (newsItems.length > 0) {
    const newsSourceIndex = dashboardDataSources.findIndex((item) => item.name === "Actualités sectorielles");
    dashboardDataSources[newsSourceIndex] = {
      ...dashboardDataSources[newsSourceIndex],
      desc: `Veille publiée via ${newsProviders.join(" · ")}`,
      status: "Connecté",
    };
  }
  // Action Center always follows the opportunity it will open. In destination scope,
  // `marketLabel` still contains the last route selected in the market picker and must
  // never override the destination-wide ranking's winning market.
  const actionMarket = actionMarketLabel(bestOpp);
  const crossInsights = buildCrossDataInsights(tourismView, mktRows, analysisScope === "market" && airLive ? MI : null, {
    selectedMarket: analysisScope === "market" ? selectedMarketRow : null,
    selectedMarketLabel: marketLabel,
  });
  const showLens = (...lenses) => dataLens === "all" || lenses.includes(dataLens);
  const visibleTourismKpis = tourismKpis.filter((item) => {
    if (dataLens === "all") return true;
    if (dataLens === "tourism") return ["arrivals", "averageStay"].includes(item.key);
    if (dataLens === "economy") return item.key === "receipts";
    if (dataLens === "hospitality") return item.key === "roomOccupancy";
    return false;
  });

  const actionContextFor = (o) => buildActionContext(o, {
    org,
    period: analysisScope === "market" ? selectedPeriod : MI.period.label,
    dataUsed: o?.source ? [o.source] : [sourceLabel],
  });
  const goExplore = (o) => navigate(o.marketKey ? `/markets/${o.marketKey}` : "/markets");
  const goCampaign = (o) => {
    if (immutableDemo) return;
    const ctx = actionContextFor(o);
    navigate("/campaigns", { state: { prefill: campaignPrefillFrom(ctx), actionContext: ctx } });
  };
  const goPartners = (o) => {
    // Honest navigation: Partners does not yet support market-based prefiltering (DEFER). Context is
    // passed in navigation state for forward compatibility only — no misleading filter is applied.
    navigate("/partners", { state: { actionContext: actionContextFor(o) } });
  };
  const runPrimaryAction = (o) => {
    if (o.primaryAction === "partners") return goPartners(o);
    if (o.primaryAction === "campaign") return goCampaign(o);
    return goExplore(o);
  };
  const openDashboardMarket = (opportunity) => {
    const origin = String(opportunity?.marketKey || "").split("-")[0].toUpperCase();
    const route = markets.find((item) => String(item.value || "").toUpperCase() === String(opportunity?.marketKey || "").toUpperCase())
      || markets.find((item) => String(item.value || "").toUpperCase().startsWith(`${origin}-`));
    if (route?.value) changeMarket(route.value);
    else goExplore(opportunity);
  };

  // Phase 3B.5.a — Lodge AI contextual drawer (Explain Opportunity). Real opportunities only.
  const [ai, setAi] = useState({ open: false, actionType: null, context: null, title: "" });
  const analyzeOpp = (o) => setAi({
    open: true, actionType: "explain_opportunity",
    context: { marketKey: o.marketKey, market: o.market, opportunityType: o.type }, title: `Analyse — ${o.market}`,
  });

  const geoScope = org.geographic_level && org.geographic_entity
    ? `${org.geographic_entity}` : (org.country || null);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div data-testid="dashboard-context-bar" className="sticky top-[76px] z-10 -mx-2 rounded-2xl border border-lodge-hairline bg-white/95 p-3 shadow-lodge backdrop-blur-xl">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-xl bg-lodge-ivory p-1" data-testid="analysis-scope-toggle">
            {[{ value: "destination", label: "Vue destination" }, { value: "market", label: "Vue marché" }].map((option) => (
              <button key={option.value} type="button" data-testid={`analysis-scope-${option.value}`} onClick={() => changeAnalysisScope(option.value)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${analysisScope === option.value ? "bg-lodge-deep text-white shadow-sm" : "text-lodge-ardoise hover:text-lodge-deep"}`}>
                {option.label}
              </button>
            ))}
          </div>
          <div className="hidden h-8 w-px bg-lodge-hairline sm:block" />
          <div className="text-xs">
            <span className="font-semibold uppercase tracking-wide text-slate-400">Destination</span>
            <span className="ml-2 font-semibold text-lodge-deep">{tourismPayload?.country || geoScope || org.country || "Non définie"}</span>
          </div>
          {analysisScope === "market" && (
            <div className="ml-0 flex items-center gap-2 lg:ml-auto">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Marché sélectionné</span>
              <Select value={market} onValueChange={changeMarket}>
                <SelectTrigger className="h-9 w-fit min-w-[170px] gap-2 rounded-xl border-lodge-hairline text-xs font-semibold text-[#1E3D2B]" data-testid="market-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {markets.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`market-opt-${m.value}`}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={`${analysisScope === "market" ? "" : "lg:ml-auto"} flex items-center gap-2`}>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Données</span>
            <Select value={dataLens} onValueChange={setDataLens}>
              <SelectTrigger className="h-9 w-[145px] rounded-xl border-lodge-hairline text-xs font-semibold text-[#1E3D2B]" data-testid="data-lens-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="tourism">Tourisme</SelectItem>
                <SelectItem value="air">Aérien</SelectItem>
                <SelectItem value="hospitality">Hôtellerie</SelectItem>
                <SelectItem value="economy">Économie</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span data-testid="analysis-period" className="ml-auto text-[11px] text-slate-400">Période : {analysisScope === "market" ? selectedPeriod : (tourismView.period || MI.period.label)}</span>
        </div>
      </div>

      {/* Non-blocking data-availability error (distinct from a normal empty/demo state). */}
      {dataError && (
        <div data-testid="dashboard-data-error" className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 sm:flex-row sm:items-center sm:justify-between">
          <span>Données momentanément indisponibles. Ceci n'indique pas une absence de données.</span>
          <button type="button" data-testid="dashboard-data-retry" onClick={retryData}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700">
            Réessayer
          </button>
        </div>
      )}

      {analysisScope === "destination" && (
        <DestinationDecisionOverview
          destinationName={isMauritius ? "Île Maurice" : localizeCountry(tourismPayload?.country || geoScope || org.country || org.name || "Destination")}
          period={tourismView.period || MI.period.label}
          tourismKpis={Object.fromEntries(tourismKpis.map((item) => [item.key, item]))}
          snapshotKpis={destinationSnapshotKpis}
          opportunities={destinationPortfolio}
          occupancy={tourismView.occupancy}
          immutableDemo={immutableDemo}
          onOpenMarket={openDashboardMarket}
          onRunAction={runPrimaryAction}
        />
      )}

      {analysisScope === "market" && (
        <MarketDecisionOverview
          marketLabel={marketLabel}
          destinationName={localizeCountry(tourismPayload?.country || geoScope || org.country || org.name || "Destination")}
          period={selectedPeriod}
          snapshotKpis={marketSnapshotKpis}
          opportunity={bestOpp}
          sourceLabel={realMarkets ? sourceLabel : "Données non disponibles"}
          immutableDemo={immutableDemo}
          onExplore={goExplore}
          onRunAction={runPrimaryAction}
          onAnalyze={analyzeOpp}
        />
      )}

      {/* Destination tourism KPIs — shown only when a ready Lodge reference series exists. */}
      {analysisScope === "market" && showLens("tourism", "hospitality", "economy") && (tourismLoading || tourismView.hasTourism || tourismView.hasOccupancy) && (
        <section className="space-y-3" data-testid="tourism-performance">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Performance touristique — contexte destination</h2>
            {!tourismLoading && <span className="rounded-full bg-lodge-mint/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-lodge-deep">Lodge Data</span>}
            {tourismView.provisional && <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Données provisoires incluses</span>}
          </div>
          {tourismLoading ? (
            <div data-testid="tourism-performance-loading" className="h-40 animate-pulse rounded-2xl border border-lodge-hairline bg-white" />
          ) : <TourismKpiGrid items={visibleTourismKpis} />}
        </section>
      )}

      {/* ===================== C — MARKET PULSE ===================== */}
      {showLens("tourism") && <section className="space-y-3" data-testid="market-pulse">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Market Pulse — demande de la destination</h2>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <SectionCard
            testid="visitor-origins-card"
            title="Origine des arrivées — destination"
            sub={originsReal ? `${sourceLabel} · Top 5 + Autres · ${mktMetric}` : `${marketLabel || "Marché"} · Top 5 + Autres`}
            demo={!originsReal && !mauritiusDemo}
            source={originsReal ? sourceLabel : undefined}
            scope={originsReal ? destinationScopeLabel : (mauritiusDemo ? undefined : demoScopeLabel)}
            scopeTone={originsReal ? "real" : "demo"}
            className="lg:col-span-1"
          >
            {!originsReal && mauritiusDemo
              ? demoUnavailable("visitor-origins-unavailable", "Aucune donnée d’origine des arrivées n’est chargée pour Maurice dans cette démonstration.", "arrivées visiteurs par marché émetteur")
              : <VisitorOriginsDonut data={topOrigins} />}
          </SectionCard>
          <SectionCard
            testid="monthly-evolution-card"
            title={marketScopeActive
              ? "Évolution mensuelle des arrivées"
              : tourismView.hasTourism ? "Évolution mensuelle — destination globale" : "Évolution — Passagers vs Visiteurs"}
            sub={marketScopeActive
              ? selectedPeriod
              : tourismView.hasTourism ? tourismView.period : "12 derniers mois"}
            demo={!marketScopeActive && !tourismView.hasTourism && !mauritiusDemo}
            source={marketScopeActive && selectedMarketEvolution
              ? selectedMarketSource
              : !marketScopeActive && tourismView.hasTourism ? "Lodge Data" : undefined}
            scope={marketScopeActive
              ? `Marché sélectionné · ${marketLabel || selectedMarketRow?.name || "marché"}`
              : !marketScopeActive ? (tourismView.hasTourism ? destinationScopeLabel : (mauritiusDemo ? undefined : demoScopeLabel)) : undefined}
            scopeTone={marketScopeActive ? (selectedMarketEvolution ? "real" : "demo") : tourismView.hasTourism ? "real" : "demo"}
            className="lg:col-span-2"
          >
            {marketScopeActive ? (
              selectedMarketEvolution ? (
                <MonthlyEvolutionChart data={selectedMarketEvolution} realTourism />
              ) : (
                <UnavailableBlock
                  testid="monthly-evolution-unavailable"
                  marketLabel={marketLabel}
                  detail={`Aucune série mensuelle n’est disponible pour ${marketLabel || "ce marché"}.`}
                  provenance="données touristiques ventilées par marché et par mois"
                />
              )
            ) : !tourismView.hasTourism && mauritiusDemo ? (
              demoUnavailable("monthly-evolution-demo-unavailable", "Aucune série mensuelle touristique n’est chargée pour Maurice dans cette démonstration.", "arrivées mensuelles de la destination")
            ) : (
              <MonthlyEvolutionChart data={tourismView.hasTourism ? tourismView.evolution : MI.monthly} realTourism={tourismView.hasTourism} />
            )}
          </SectionCard>
        </div>
      </section>}

      {/* ===================== D — SEASONALITY ===================== */}
      {showLens("tourism") && <section className="space-y-3" data-testid="seasonality">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Seasonality — contexte destination</h2>
        <SectionCard
          testid="tourism-seasonality-card"
          title="Saisonnalité des visiteurs"
          sub={marketScopeActive && selectedMarketSeasonality
            ? `Indice mensuel (moyenne = 100) · ${selectedPeriod}`
            : tourismView.hasTourism
              ? `Indice mensuel (moyenne = 100) · ${tourismView.period}`
              : "Indice mensuel (base 100) · périodes fortes / faibles"}
          demo={!marketScopeActive && !tourismView.hasTourism && !mauritiusDemo}
          source={marketScopeActive && selectedMarketSeasonality
            ? selectedMarketSource
            : !marketScopeActive && tourismView.hasTourism ? "Lodge Data" : undefined}
          scope={marketScopeActive && selectedMarketSeasonality
            ? `Marché sélectionné · ${marketLabel || selectedMarketRow?.name || "marché"}`
            : !marketScopeActive ? (tourismView.hasTourism ? destinationScopeLabel : (mauritiusDemo ? undefined : demoScopeLabel)) : undefined}
          scopeTone={marketScopeActive && selectedMarketSeasonality ? "real" : tourismView.hasTourism ? "real" : "demo"}
        >
          {marketScopeActive ? (
            selectedMarketSeasonality ? (
              <SeasonalityChart data={selectedMarketSeasonality} />
            ) : (
              <UnavailableBlock
                testid="seasonality-unavailable"
                marketLabel={marketLabel}
                detail={`Aucune série mensuelle n’est disponible pour ${marketLabel || "ce marché"}.`}
                provenance="arrivées mensuelles ventilées par marché"
              />
            )
          ) : !tourismView.hasTourism && mauritiusDemo ? (
            demoUnavailable("seasonality-demo-unavailable", "La saisonnalité des visiteurs n’est pas chargée pour Maurice dans cette démonstration.", "arrivées mensuelles de la destination")
          ) : (
            <SeasonalityChart data={tourismView.hasTourism ? tourismView.seasonality : MI.visitorSeasonality} />
          )}
        </SectionCard>
      </section>}

      {showLens("hospitality") && tourismView.hasOccupancy && (
        <section className="space-y-3" data-testid="hotel-occupancy">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Hospitality — contexte destination</h2>
          <SectionCard testid="hotel-occupancy-card" title="Occupation mensuelle des hôtels" sub={tourismView.occupancyPeriod} source="Lodge Data">
            <OccupancyChart data={tourismView.occupancy} />
          </SectionCard>
        </section>
      )}

      {/* ===================== E — AIR & ACCESSIBILITY ===================== */}
      {analysisScope === "market" && showLens("air") && <section className="space-y-3" data-testid="air-accessibility">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Air &amp; Accessibility{marketLabel ? ` — ${marketLabel}` : ""}</h2>
        {isMauritius && airLoading ? (
          <div data-testid="mauritius-air-loading" className="h-48 animate-pulse rounded-2xl border border-lodge-hairline bg-white" />
        ) : isMauritius && !airLive ? (
          <div data-testid="mauritius-air-unavailable" className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-6 text-sm text-amber-900">
            Les données aériennes {marketLabel || "de la liaison sélectionnée"} sont momentanément indisponibles. Aucun exemple générique n’est affiché à leur place.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <SectionCard testid="air-passengers-card" title="Top compagnies par passagers" sub={MI.period.label} demo={!airLive} source={airLive ? MI.airSource : undefined}>
              <AirlineRanking items={MI.topAirlinesByPassengers} unit="M" formatItem={(a) => (a.raw != null ? fmtPaxStr(a.raw) : `${a.value} M`)} footerLabel="Voir toutes les compagnies" testid="ranking-passengers" />
            </SectionCard>
            <SectionCard testid="air-revenue-card" title="Top compagnies par revenus" sub={MI.period.label} demo={!airLive} source={airLive ? MI.airSource : undefined}>
              <AirlineRanking items={MI.topAirlinesByRevenue} valueFormat={(v) => `${v} Md $`} formatItem={(a) => (a.raw != null ? fmtRevStr(a.raw) : `${a.value} Md $`)} footerLabel="Voir toutes les compagnies" testid="ranking-revenue" />
            </SectionCard>
          </div>
        )}
      </section>}

      {analysisScope === "market" && showLens("air") && airLive && <AirInsights mi={MI} />}

      {analysisScope === "market" && showLens("air") && market === "FR-MRU" && (
        <section className="space-y-3" data-testid="bsp-agency-distribution">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Distribution BSP — agences France → Maurice</h2>
          {bspLoading ? (
            <div className="h-48 animate-pulse rounded-2xl border border-lodge-hairline bg-white" />
          ) : !bspData?.period ? (
            <UnavailableBlock testid="bsp-agencies-unavailable" marketLabel="France → Maurice"
              detail="Aucun dataset BSP agence validé n’est disponible pour Maurice."
              provenance="ventes BSP par agence et destination" />
          ) : (
            <SectionCard testid="bsp-agencies-card" title="Principales agences distributrices"
              sub={`${bspData.period.start} → ${bspData.period.end} · ${bspData.currency}`}
              source="Lodge Data">
              <div className="mb-5 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-[#FAFAF6] px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">Agences observées</p>
                  <p className="font-mono text-lg font-semibold text-[#1E3D2B]">{Number(bspData.agency_count || 0).toLocaleString("fr-FR")}</p>
                </div>
                <div className="rounded-lg bg-[#FAFAF6] px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">Ventes industrie</p>
                  <p className="font-mono text-lg font-semibold text-[#1E3D2B]">{Number(bspData.total_industry_sales || 0).toLocaleString("fr-FR", { style: "currency", currency: bspData.currency, maximumFractionDigits: 0 })}</p>
                </div>
                <div className="rounded-lg bg-[#FAFAF6] px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">Documents BSP</p>
                  <p className="font-mono text-lg font-semibold text-[#1E3D2B]">{Number(bspData.total_industry_documents || 0).toLocaleString("fr-FR")}</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <tr><th className="py-2 pr-3">Agence</th><th className="px-3 py-2 text-right">Ventes industrie</th><th className="px-3 py-2 text-right">Part</th><th className="pl-3 py-2 text-right">Documents</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(bspData.top_agencies || []).slice(0, 10).map((agency) => (
                      <tr key={`${agency.rank}-${agency.agency_name}`}>
                        <td className="py-2.5 pr-3 font-medium text-slate-700">{agency.rank}. {agency.agency_name}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-700">{Number(agency.industry_sales || 0).toLocaleString("fr-FR", { style: "currency", currency: bspData.currency, maximumFractionDigits: 0 })}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-500">{Number(agency.sales_share || 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %</td>
                        <td className="pl-3 py-2.5 text-right font-mono text-slate-500">{Number(agency.industry_documents || 0).toLocaleString("fr-FR")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-xs text-slate-400">Données BSP industrie agrégées. Les coordonnées des agences et les ventes propres à une compagnie ne sont pas exposées.</p>
            </SectionCard>
          )}
        </section>
      )}

      {crossInsights.length > 0 && (
        <section className="space-y-3" data-testid="cross-data-conclusions">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Conclusions croisées — aérien × arrivées × occupation</h2>
            <span className="rounded-full bg-lodge-mint/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-lodge-deep">Calcul déterministe</span>
          </div>
          <CrossDataInsights items={crossInsights} />
        </section>
      )}

      {/* ===================== F — LODGE OPPORTUNITIES ===================== */}
      {analysisScope === "market" && <section className="space-y-3" data-testid="lodge-opportunities">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles size={16} className="text-[#3E7256]" />
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Lodge Opportunities</h2>
          <span data-testid="opps-mode-badge" className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${oppsAreDemo ? "bg-amber-100 text-amber-700" : "bg-lodge-mint/20 text-lodge-deep"}`}>
            {oppsAreDemo ? (mauritiusDemo ? "Non disponible" : "Demo") : "Données réelles"}
          </span>
          {!oppsAreDemo && analysisScope === "market" && selectedMarketRow && (
            <span data-testid="opps-market-scope" className="rounded-full border border-lodge-hairline bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-lodge-deep">
              Marché sélectionné · {marketLabel || selectedMarketRow.name}
            </span>
          )}
          <span className="ml-auto text-[11px] text-slate-400">Data → Signal → Opportunity → Action</span>
        </div>

        {oppsAreDemo && mauritiusDemo && demoUnavailable("opps-demo-unavailable", "Aucune opportunité n’est calculée : les données touristiques par marché ne sont pas chargées. Aucun exemple générique n’est affiché.", "arrivées et évolutions par marché")}

        {oppsAreDemo && !mauritiusDemo && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {opps.map((o, i) => (
              <div key={o.id} data-testid={`dash-opp-${i}`} className="flex flex-col justify-between rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Sparkles size={15} className="text-amber-500" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{o.objective || "Exemple"}</span>
                    <span data-testid={`opp-prov-${i}`} className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Demo</span>
                  </div>
                  <p className="text-sm font-medium leading-snug text-[#1E3D2B]">{o.opportunity}</p>
                  <p className="mt-1.5 text-xs text-slate-400">{o.evidence}</p>
                </div>
                <button type="button" data-testid={`opp-cta-explore-${i}`} onClick={() => goExplore(o)}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
                  Explorer les marchés <ArrowUpRight size={13} />
                </button>
                <button type="button" data-testid={`opp-cta-campaign-disabled-${i}`} disabled title="Importez vos données pour créer une campagne à partir de cette opportunité."
                  className="mt-2 inline-flex cursor-not-allowed items-center gap-1.5 text-xs font-semibold text-slate-300">
                  <Megaphone size={13} /> Campagne <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-400">Demo</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {!oppsAreDemo && (
          opps.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {opps.map((o, i) => {
                const Icon = OPP_ICON[o.type] || Sparkles;
                return (
                  <div key={o.id} data-testid={`dash-opp-${i}`} className="flex flex-col justify-between rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm transition-all hover:border-[#3E7256] hover:shadow-sm">
                    <div>
                      <div className="mb-3 flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-lodge-mint/20 text-lodge-deep"><Icon size={15} /></span>
                        <span data-testid={`opp-signal-${i}`} className="text-sm font-semibold text-[#1E3D2B]">{o.signal}</span>
                        <span data-testid={`opp-conf-${i}`} className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${CONF_PILL[o.confidence] || CONF_PILL.Low}`}>{o.confidence}</span>
                      </div>
                      <p data-testid={`opp-evidence-${i}`} className="text-xs leading-relaxed text-slate-500">{o.evidence}</p>
                      <p data-testid={`opp-opportunity-${i}`} className="mt-3 text-sm font-medium leading-snug text-[#1E3D2B]">{o.opportunity}</p>
                      <div className="mt-2">
                        <span data-testid={`opp-prov-${i}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${PROV_PILL[o.source] || PROV_PILL.Demo}`}>{o.source}</span>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
                      <span className="text-[11px] font-medium text-slate-400">{o.recommendedAction}</span>
                      <div className="ml-auto flex items-center gap-3">
                        {o.marketKey && (
                          <button type="button" data-testid={`opp-cta-ai-${i}`} onClick={() => analyzeOpp(o)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
                            <Sparkles size={13} /> Lodge AI
                          </button>
                        )}
                        <button type="button" data-testid={`opp-cta-explore-${i}`} onClick={() => goExplore(o)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
                          <Compass size={13} /> Explorer
                        </button>
                        {o.type === "concentration" ? (
                          <button type="button" data-testid={`opp-cta-partners-${i}`} onClick={() => goPartners(o)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
                            <Users size={13} /> Partenaires
                          </button>
                        ) : immutableDemo ? (
                          <button type="button" data-testid={`opp-cta-campaign-disabled-${i}`} disabled
                            className="inline-flex cursor-not-allowed items-center gap-1 text-xs font-semibold text-slate-300">
                            <Megaphone size={13} /> Campagne
                          </button>
                        ) : (
                          <button type="button" data-testid={`opp-cta-campaign-${i}`} onClick={() => goCampaign(o)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
                            <Megaphone size={13} /> Campagne
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div data-testid="opps-context-empty" className="rounded-2xl border border-lodge-hairline bg-white px-5 py-6 text-sm text-slate-500">
              Aucune opportunité déterministe n’est disponible pour {marketLabel || selectedMarketRow?.name || "ce marché"} avec les données actuelles.
            </div>
          )
        )}
      </section>}

      {/* ===================== G — ACTION CENTER ===================== */}
      {analysisScope === "market" && <section className="space-y-3" data-testid="action-center">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Action Center</h2>

        {/* A — Recommended action (contextual, real opportunities only). Compact, does not compete with Lodge Opportunities. */}
        {bestOpp ? (
          <div data-testid="recommended-action" className="flex flex-col gap-3 rounded-2xl border border-[#BBD8C5] bg-[#F5F9F6] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#3E7256]">Action recommandée</p>
              <p className="mt-1 text-sm font-semibold text-[#1E3D2B]" data-testid="recommended-signal">{bestOpp.signal}</p>
              <p className="text-sm text-[#2C5540]" data-testid="recommended-opportunity">{bestOpp.opportunity}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span data-testid="recommended-provenance" className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${PROV_PILL[bestOpp.source] || PROV_PILL.Demo}`}>{bestOpp.source}</span>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${CONF_PILL[bestOpp.confidence] || CONF_PILL.Low}`}>{bestOpp.confidence} confidence</span>
                <span className="text-[11px] text-slate-400">{bestOpp.recommendedAction}</span>
              </div>
            </div>
            <button type="button" data-testid="recommended-cta" onClick={() => runPrimaryAction(bestOpp)}
              disabled={immutableDemo && bestOpp.primaryAction === "campaign"}
              className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${immutableDemo && bestOpp.primaryAction === "campaign" ? "cursor-not-allowed bg-slate-200 text-slate-400" : "bg-[#1E3D2B] text-white hover:bg-[#2C5540]"}`}>
              {bestOpp.primaryAction === "partners" ? <><Users size={15} /> Trouver des partenaires</>
                : bestOpp.primaryAction === "campaign" ? <><Megaphone size={15} /> Créer une campagne</>
                : <><Compass size={15} /> Explorer le marché</>}
            </button>
          </div>
        ) : (
          <p data-testid="recommended-action-empty" className="text-xs text-slate-400">
            {!oppsAreDemo && analysisScope === "market" && selectedMarketRow
              ? `Aucune action déterministe disponible pour ${marketLabel || selectedMarketRow.name} avec les données actuelles.`
              : "Importez vos données pour obtenir des actions recommandées."}
          </p>
        )}

        {/* B — Actions inherit the selected market when a contextual opportunity exists. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <ActionButton icon={Compass} label="Explore Market" sub={bestOpp ? `Analyser : ${actionMarket}` : "Analyser marchés & opportunités"}
            onClick={bestOpp ? () => goExplore(bestOpp) : () => navigate("/markets")} testid="action-explore-market" />
          <ActionButton icon={Megaphone} label="Build Campaign" sub={bestOpp ? `Créer pour : ${actionMarket}` : "Créer une campagne"}
            disabled={immutableDemo}
            onClick={immutableDemo ? undefined : (bestOpp ? () => goCampaign(bestOpp) : () => navigate("/campaigns"))} testid="action-build-campaign" />
          <ActionButton icon={Users} label="Find Partners" sub={bestOpp ? `Partenaires : ${actionMarket}` : "Trouver des partenaires"}
            onClick={bestOpp ? () => goPartners(bestOpp) : () => navigate("/partners")} testid="action-find-partners" />
          <ActionButton icon={Bot} label="Analyze with AI" sub={bestOpp ? `Analyser : ${actionMarket}` : "Recommandations Lodge AI"}
            disabled={!bestOpp}
            onClick={bestOpp ? () => analyzeOpp(bestOpp) : undefined} testid="action-analyze-ai" />
        </div>
      </section>}

      <LodgeAiDrawer open={ai.open} onClose={() => setAi((a) => ({ ...a, open: false }))}
        orgId={activeOrgId} actionType={ai.actionType} context={ai.context} title={ai.title}
        demoSimulation={immutableDemo} demoVariant={mauritiusDemo ? "mauritius" : undefined} />

      {/* ===================== H — YOUR DATA ===================== */}
      <section className="space-y-4 border-t border-lodge-hairline pt-8" data-testid="real-data-zone">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Vos données</h2>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-600">Client Data</span>
        </div>

        {!hasRealData && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="real-data-empty">
            <p className="text-sm text-slate-500">Aucune donnée importée pour l'instant.</p>
            <a href="/data-hub" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
              Importer un fichier <ArrowRight size={14} />
            </a>
          </div>
        )}

        {hasImported && (
          <section data-testid="market-performance" className="rounded-2xl border border-lodge-hairline bg-white p-6 shadow-lodge-sm lg:p-8">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="font-heading text-base font-semibold tracking-tight text-[#1E3D2B]">{t("sec_perf_title")}</h3>
                <p className="mt-1 text-sm text-slate-400">Basé sur vos données importées · {imported.data_type}</p>
              </div>
              <div className="flex items-center gap-2">
                {imported.available_types.length > 0 && (
                  <Select value={imported.data_type} onValueChange={setSelectedType}>
                    <SelectTrigger className="h-9 w-fit gap-2 rounded-full border-slate-200 text-xs font-medium" data-testid="dashboard-type-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {imported.available_types.map((tp) => (
                        <SelectItem key={tp} value={tp} data-testid={`dashboard-type-${tp}`}>{tp}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <span data-testid="imported-data-badge" className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">Données réelles</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={imported.performance} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="perfFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6FC49B" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#6FC49B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F0E7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} dy={8} />
                <YAxis tick={{ fontSize: 11, fill: "#CBD5E1" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#0B3B36" strokeWidth={2.5} fill="url(#perfFill)" isAnimationActive={false} dot={false} activeDot={{ r: 4, fill: "#0B3B36", strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          </section>
        )}

        {dim && dim.categories.length > 0 && (
          <section data-testid="dimension-analysis" className="rounded-2xl border border-lodge-hairline bg-white p-6 shadow-lodge-sm lg:p-8">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="font-heading text-base font-semibold tracking-tight text-[#1E3D2B]">Analyse par dimension</h3>
                <p className="mt-1 text-sm text-slate-400">
                  Répartition {dim.mode === "count" ? "par nombre d'enregistrements" : "par valeur"} de « {dim.data_type} »
                  {dim.temporal ? "" : " · analyse non chronologique"}.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {dim.available_types.length > 1 && (
                  <Select value={dim.data_type} onValueChange={setDimType}>
                    <SelectTrigger className="h-9 w-fit gap-2 rounded-full border-slate-200 text-xs font-medium" data-testid="dimension-type-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dim.available_types.map((tp) => (
                        <SelectItem key={tp} value={tp} data-testid={`dimension-type-${tp}`}>{tp}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                  {dim.category_count} dimension{dim.category_count > 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="mb-6 flex flex-wrap gap-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Total</p>
                <p className="mt-1 font-heading text-2xl font-bold text-[#1E3D2B]" data-testid="dimension-total">{dim.total.toLocaleString("fr-FR")}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Dimension principale</p>
                <p className="mt-1 font-heading text-2xl font-bold text-[#1E3D2B]">{dim.categories[0].name}</p>
              </div>
            </div>
            <div className="max-h-96 space-y-5 overflow-auto pr-1">
              {dim.categories.map((c, i) => (
                <div key={c.name} data-testid={`dimension-row-${i}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="truncate text-sm font-medium text-slate-700" title={c.name}>{c.name}</span>
                    <span className="ml-3 shrink-0 font-mono text-xs font-semibold text-[#1E3D2B]">
                      {c.value.toLocaleString("fr-FR")} <span className="text-slate-400">· {c.pct}%</span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-lodge-mint" style={{ width: `${c.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </section>

      {/* ===================== NATIONAL BENCHMARK (CITY/REGION, secondary CONTEXT) ===================== */}
      {benchmark && benchTop.length > 0 && (
        <section className="space-y-3 border-t border-lodge-hairline pt-8" data-testid="national-benchmark">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">
              Contexte national — {benchmark.benchmark_scope?.entity || marketContext.country}
            </h2>
            <span data-testid="benchmark-badge" className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Benchmark</span>
            {benchmarkProvenance && <span className="text-[11px] text-slate-400" data-testid="benchmark-provenance">{benchmarkProvenance.source}</span>}
          </div>
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-5" data-testid="national-benchmark-card">
            <p className="mb-4 max-w-3xl text-xs text-slate-500">
              Référence nationale permettant de comparer la dynamique de votre destination.
              Donnée <span className="font-semibold text-slate-600">de contexte</span>, distincte de la performance de votre territoire.
              {benchMetric && <> Métrique&nbsp;: <span className="font-semibold text-slate-600">{benchMetric}</span>.</>}
            </p>
            <div className="overflow-hidden rounded-xl border border-indigo-100 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-indigo-100 bg-indigo-50/60 text-left text-[11px] font-semibold uppercase tracking-wider text-indigo-700">
                    <th className="px-4 py-2.5">Marché émetteur</th>
                    <th className="px-4 py-2.5">{benchMetric || "Valeur"}</th>
                    <th className="px-4 py-2.5">Évolution</th>
                  </tr>
                </thead>
                <tbody>
                  {benchTop.map((m) => (
                    <tr key={m.key || m.name} data-testid={`benchmark-row-${m.name}`} className="border-b border-indigo-50 last:border-0">
                      <td className="px-4 py-2.5 font-medium text-[#1E3D2B]">{m.name}</td>
                      <td className="px-4 py-2.5 font-mono text-slate-700">{m.volume == null ? "—" : m.volume.toLocaleString("fr-FR")}</td>
                      <td className="px-4 py-2.5 text-slate-600">{m.evolution == null ? "—" : `${m.evolution > 0 ? "+" : ""}${m.evolution}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ===================== I — SECONDARY: economic context + sources + signals ===================== */}
      <section className="space-y-6 border-t border-lodge-hairline pt-8" data-testid="secondary-modules">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <SectionCard testid="economic-context-card" title="Contexte économique" sub="Banque mondiale · BCE" className="lg:col-span-2">
            {marketScopeActive ? (
              economicLoading
                ? <p className="text-sm text-slate-400" data-testid="economic-context-loading">Chargement des indicateurs officiels…</p>
                : economicError
                  ? <UnavailableBlock testid="economic-context-error" title="Contexte économique momentanément indisponible" detail="Le service n’a pas pu être interrogé. Aucune valeur de remplacement n’est affichée." provenance="Banque mondiale et Banque centrale européenne" />
                  : economicItems.length > 0
                    ? <EconomicContextCard items={economicItems} />
                    : <UnavailableBlock testid="economic-context-unavailable" marketLabel={marketLabel} detail="Aucun jeu de données macroéconomiques publié ne correspond encore à ce marché." provenance="Banque mondiale et Banque centrale européenne" />
            ) : <UnavailableBlock testid="economic-context-market-required" title="Sélectionnez un marché émetteur" detail="Le contexte économique est rattaché au pays d’origine du marché sélectionné." provenance="Banque mondiale et Banque centrale européenne" />}
          </SectionCard>
          <SectionCard testid="data-sources-card" title="Sources de données" sub={airLive || macroProviders.length ? "Sources utilisées" : "Connexions à venir"}>
            <DataSourcesCard items={dashboardDataSources} />
          </SectionCard>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SectionCard testid="lodge-intelligence-card" title="Lodge Intelligence — signaux" sub="Détectés automatiquement" demo={!marketScopeActive && !mauritiusDemo} scope={!marketScopeActive && !mauritiusDemo ? demoScopeLabel : undefined} right={<Sparkles size={18} className="text-[#3E7256]" />}>
            {marketScopeActive ? (
              signalsLoading
                ? <p className="text-sm text-slate-400" data-testid="lodge-intelligence-loading">Analyse des données observées…</p>
                : signalsError
                  ? <UnavailableBlock testid="lodge-intelligence-error" title="Signaux momentanément indisponibles" detail="Le service d’analyse n’a pas pu être interrogé. Aucun signal de remplacement n’est affiché." provenance="données publiées Lodge Data et sources macro officielles" />
                  : signalsMissing
                    ? <UnavailableBlock testid="lodge-intelligence-market-missing" title="Marché non disponible" detail="Aucune donnée touristique observée ne correspond à ce marché dans cette organisation." provenance="données publiées Lodge Data et sources macro officielles" />
                  : intelligenceSignals.length > 0
                    ? <LodgeIntelligenceSignals signals={intelligenceSignals} />
                    : <UnavailableBlock testid="lodge-intelligence-no-signals" title="Aucun signal détecté" detail="Les signaux apparaissent uniquement lorsqu’un croisement déterministe est étayé par les données touristiques, aériennes ou macroéconomiques disponibles." provenance="données publiées Lodge Data et sources macro officielles" />
            ) : mauritiusDemo ? demoUnavailable("lodge-intelligence-demo-unavailable", "Aucun signal n’est affiché sans données touristiques observées.", "signaux croisés tourisme × aérien") : <LodgeIntelligenceSignals signals={MI.intelligenceSignals} />}
          </SectionCard>
          <SectionCard testid="live-signals-card" title="News / Market Signals" sub="Veille sectorielle sourcée">
            {marketScopeActive ? (
              newsLoading
                ? <p className="text-sm text-slate-400" data-testid="live-signals-loading">Chargement de la veille publiée…</p>
                : newsError
                  ? <UnavailableBlock testid="live-signals-error" title="Veille momentanément indisponible" detail="Le service n’a pas pu être interrogé. Aucun article de remplacement n’est affiché." provenance="articles publiés et liens vers les éditeurs" />
                  : newsItems.length > 0
                    ? <LiveMarketSignals items={newsItems} />
                    : <UnavailableBlock testid="live-signals-unavailable" marketLabel={marketLabel} detail="Aucun article sectoriel conforme provenant du marché émetteur n’est actuellement disponible. Aucun article d’un autre pays n’est affiché à sa place." provenance="presse du marché émetteur, articles datés et liens vers les éditeurs" />
            ) : <UnavailableBlock testid="live-signals-market-required" title="Sélectionnez un marché émetteur" detail="La veille sectorielle est sourcée dans la presse du pays d’origine du marché sélectionné." provenance="articles datés et liens vers les éditeurs" />}
          </SectionCard>
        </div>
      </section>
    </div>
  );
}
